import { AreaChart } from '@mantine/charts';
import {
	Button,
	Center,
	Flex,
	Grid,
	Group,
	Paper,
	ScrollArea,
	Skeleton,
	Stack,
	Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
	IconChevronDown,
	IconChevronUp,
	IconTargetArrow,
} from '@tabler/icons-react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { NoDataFoundMessage } from '../../components/DataTable';
import { DateRangePicker } from '../../components/DateRangePicker';
import { PageHeader } from '../../components/PageHeader';
import { LIGHT_GREY } from '../../const/constants';
import { useFilter } from '../../hooks/useFilter';
import { defaultIconStyle } from '../../theme/styling/iconStyling';
import { queryWithFilter } from '../../util/api/queryWithFilter';
import {
	formatToCurrency,
	formatToDateString,
	formatToPercentage,
} from '../../util/formatting';
import { NewWeeklyTarget } from './components/NewWeeklyTargetModal';
import type { WeeklyTargetDto } from './types/WeeklyTargetDto';

export function WeeklyTargets() {
	const filter = useFilter();
	const { organisation } = useParams();

	const weeklyTargetsQuery = queryWithFilter<WeeklyTargetDto[]>(
		`${organisation}/weekly-targets/all`,
		filter.value,
	);

	const [showNewModal, { open: openNewModal, close: closeNewModal }] =
		useDisclosure(false);

	useEffect(() => {
		document.body.style.backgroundColor = LIGHT_GREY;
	}, []);

	return (
		<>
			<NewWeeklyTarget
				close={closeNewModal}
				opened={showNewModal}
				triggerListUpdate={() => weeklyTargetsQuery.remove()}
			/>
			<PageHeader title={'Weekly Targets'} hideFiltering>
				<Group>
					<DateRangePicker filter={filter} variant={'default'} isWeekly />
					<Button
						color={'medepos.1'}
						variant={'filled'}
						leftSection={<IconTargetArrow {...defaultIconStyle} />}
						onClick={() => openNewModal()}
					>
						New Target
					</Button>
				</Group>
			</PageHeader>
			<Stack w={'100%'}>
				<ScrollArea>
					{weeklyTargetsQuery.data?.value.map((target) => (
						<Paper radius={'sm'} p={10} px={20} h={'15vh'} withBorder mb={10}>
							<Grid w={'100%'} gutter={20}>
								<Grid.Col span={9}>
									<Stack>
										<Group>
											<Text size={'sm'} fw={500} mr={-10} pr={-10}>
												Week Starting {formatToDateString(target.startDate)}
											</Text>
											<Text size={'sm'} c={'grey'}>
												<i>Ending {formatToDateString(target.endDate)}</i>
											</Text>
										</Group>
										<Center>
											{target.endOfDayNetSales.length > 0 ? (
												<>
													<AreaChart
														withDots={false}
														h={'6vh'}
														data={mapToChartData(target)}
														dataKey={'date'}
														type={'stacked'}
														withYAxis={false}
														withXAxis={false}
														valueFormatter={(value) => formatToCurrency(value)}
														gridAxis={'none'}
														strokeWidth={1}
														series={[
															{
																label: 'Net Amount',
																name: 'netAmount',
																color: 'indigo.6',
															},
														]}
													/>
												</>
											) : (
												<Text>No data to display</Text>
											)}
										</Center>
									</Stack>
								</Grid.Col>
								<Grid.Col span={2}>
									<Flex h={'100%'} align="center">
										<Stack h={'6vh'}>
											<Text size={'xl'}>
												<b>{formatToCurrency(target.progress)}</b>
												<Text span size={'sm'}>
													/ {formatToCurrency(target.target)}
												</Text>
											</Text>
											<Text c={'grey'} size={'sm'} mt={-15}>
												{target.target - target.progress > 0 ? (
													<>
														{formatToCurrency(
															target.progress - target.target,
														).toString()}{' '}
														<i>
															{' '}
															(
															{formatToPercentage(
																(target.target - target.progress) /
																	target.target,
															)}
															){' '}
														</i>
													</>
												) : (
													<>
														+
														{formatToCurrency(
															target.progress - target.target,
														).toString()}{' '}
														<i>
															{' '}
															(
															{formatToPercentage(
																target.target / target.progress,
															)}
															)
														</i>
													</>
												)}
											</Text>
										</Stack>
									</Flex>
								</Grid.Col>
								<Grid.Col span={1}>
									<Flex h={'100%'} align="center">
										{target.target - target.progress >= 0 ? (
											<IconChevronDown size={'2.25rem'} color={'#9E544F'} />
										) : (
											<IconChevronUp size={'2.25rem'} color={'#708090'} />
										)}
									</Flex>
								</Grid.Col>
							</Grid>
						</Paper>
					))}
					{weeklyTargetsQuery.isLoading &&
						Array.from({ length: 10 }).map((_, index) => (
							<Paper radius={'sm'} p={10} px={20} h={'15vh'} withBorder mb={10}>
								<Skeleton h={'100%'} w={'100%'} />
							</Paper>
						))}
					{weeklyTargetsQuery.data?.value.length === 0 && (
						<NoDataFoundMessage />
					)}
				</ScrollArea>
			</Stack>
		</>
	);
}

function calculatePercentRemaining(
	originalAmount: number,
	remainingAmount: number,
): number {
	if (originalAmount === 0) {
		throw new Error('Original amount cannot be zero.');
	}
	const percentRemaining = (remainingAmount / originalAmount) * 100;
	return percentRemaining;
}

export function mapToChartData(
	eodLines?: WeeklyTargetDto,
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
): Record<string, any>[] {
	if (eodLines == null) {
		return noDataToDisplay;
	}

	return eodLines.endOfDayNetSales.map((eodLogs) => {
		return {
			date: formatToDateString(eodLogs.dateTime),
			netAmount: eodLogs.value,
		};
	});
}

const noDataToDisplay = [
	{
		date: 'No Data',
		netAmount: 0,
	},
];
